import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  MultiSelect,
  PasswordInput,
  Select,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { UserDto } from './types';
import { useCreateUser, useUpdateUser } from './usersApi';
import { useRoles } from '../roles/rolesApi';
import { useBranches } from '../branches/branchesApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  user: UserDto | null;
}

interface FormValues {
  fullNameAr: string;
  fullNameEn: string;
  email: string;
  phoneNumber: string;
  password: string;
  isActive: boolean;
  roleIds: string[];
  branchId: string | null;
}

export function UserFormDrawer({ opened, onClose, user }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateUser();
  const update = useUpdateUser();
  const rolesQuery = useRoles();
  const branchesQuery = useBranches();
  const submitting = create.isPending || update.isPending;
  const editing = user !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      fullNameAr: '',
      fullNameEn: '',
      email: '',
      phoneNumber: '',
      password: '',
      isActive: true,
      roleIds: [],
      branchId: null,
    },
    validate: {
      fullNameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      password: (v) =>
        !editing && v.length < 8 ? t('users.form.passwordTooShort') : null,
      email: (_, values) =>
        !values.email.trim() && !values.phoneNumber.trim()
          ? t('users.form.contactRequired')
          : null,
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      fullNameAr: user?.fullNameAr ?? '',
      fullNameEn: user?.fullNameEn ?? '',
      email: user?.email ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      password: '',
      isActive: user?.isActive ?? true,
      roleIds: user?.roleIds ?? [],
      branchId: user?.branchId ?? null,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, user]);

  const branchOptions = (branchesQuery.data ?? []).map((b) => ({
    value: b.branchId,
    label: i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr),
  }));

  const roleOptions = (rolesQuery.data ?? []).map((r) => ({
    value: r.roleId,
    label: i18n.language === 'ar' ? r.nameAr : (r.nameEn ?? r.nameAr),
  }));

  const submit = async (values: FormValues) => {
    const email = values.email.trim() || null;
    const phoneNumber = values.phoneNumber.trim() || null;
    const fullNameEn = values.fullNameEn.trim() || null;
    try {
      if (editing) {
        await update.mutateAsync({
          id: user.userId,
          request: {
            fullNameAr: values.fullNameAr.trim(),
            email,
            phoneNumber,
            fullNameEn,
            isActive: values.isActive,
            roleIds: values.roleIds,
            branchId: values.branchId,
          },
        });
      } else {
        await create.mutateAsync({
          fullNameAr: values.fullNameAr.trim(),
          password: values.password,
          email,
          phoneNumber,
          fullNameEn,
          roleIds: values.roleIds,
          branchId: values.branchId,
        });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: values.fullNameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('users.form.editTitle') : t('users.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('users.form.nameArLabel')}
            withAsterisk
            {...form.getInputProps('fullNameAr')}
          />
          <TextInput
            label={t('users.form.nameEnLabel')}
            {...form.getInputProps('fullNameEn')}
          />
          <TextInput
            label={t('users.form.emailLabel')}
            placeholder="user@example.com"
            description={t('users.form.contactHelp')}
            {...form.getInputProps('email')}
          />
          <TextInput
            label={t('users.form.phoneLabel')}
            placeholder="+970…"
            {...form.getInputProps('phoneNumber')}
          />
          {!editing && (
            <PasswordInput
              label={t('users.form.passwordLabel')}
              description={t('users.form.passwordHelp')}
              withAsterisk
              {...form.getInputProps('password')}
            />
          )}
          <Select
            label={t('users.form.branchLabel')}
            description={t('users.form.branchHelp')}
            placeholder={t('users.form.branchPlaceholder')}
            data={branchOptions}
            value={form.values.branchId}
            onChange={(v) => form.setFieldValue('branchId', v)}
            searchable
            clearable
            disabled={branchesQuery.isLoading}
          />
          <MultiSelect
            label={t('users.form.rolesLabel')}
            placeholder={t('users.form.rolesPlaceholder')}
            data={roleOptions}
            value={form.values.roleIds}
            onChange={(value) => form.setFieldValue('roleIds', value)}
            searchable
            clearable
            disabled={rolesQuery.isLoading}
          />
          {editing && (
            <Switch
              label={t('users.form.activeLabel')}
              description={t('users.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
